import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// ─── Auth interceptor ─────────────────────────────────────────────────────
let _onUnauthorized = null;
export const setOnUnauthorized = (cb) => { _onUnauthorized = cb; };

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let _refreshing = null;
async function tryRefresh() {
  if (_refreshing) return _refreshing;
  const refresh = localStorage.getItem("refresh_token");
  if (!refresh) return Promise.reject(new Error("no refresh"));
  _refreshing = axios.post(`${API}/auth/refresh`, { refresh_token: refresh })
    .then((r) => {
      localStorage.setItem("access_token", r.data.access_token);
      localStorage.setItem("refresh_token", r.data.refresh_token);
      return r.data;
    })
    .finally(() => { _refreshing = null; });
  return _refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config;
    if (err.response?.status === 401 && !orig._retry && !orig.url.includes("/auth/")) {
      orig._retry = true;
      try {
        await tryRefresh();
        orig.headers.Authorization = `Bearer ${localStorage.getItem("access_token")}`;
        return api.request(orig);
      } catch {
        if (_onUnauthorized) _onUnauthorized();
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  },
);

// ─── Auth ────────────────────────────────────────────────────────────────
export const authLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const authLogout = (refresh_token) =>
  api.post("/auth/logout", { refresh_token }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const changePassword = (current_password, new_password) =>
  api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);

// ─── Users (admin) ───────────────────────────────────────────────────────
export const listUsers = () => api.get("/users").then((r) => r.data);
export const createUser = (data) => api.post("/users", data).then((r) => r.data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data).then((r) => r.data);
export const resetUserPassword = (id, new_password) =>
  api.post(`/users/${id}/reset-password`, { new_password }).then((r) => r.data);

// Produttori
export const getProduttori = (params) => api.get("/produttori", { params }).then((r) => r.data);
export const getProduttore = (id) => api.get(`/produttori/${id}`).then((r) => r.data);
export const updateProduttore = (id, data) => api.put(`/produttori/${id}`, data).then((r) => r.data);
export const createProduttore = (data) => api.post("/produttori", data).then((r) => r.data);
export const deleteProduttore = (id) => api.delete(`/produttori/${id}`).then((r) => r.data);

// Blocco B6.3A — Dashboard revisione produttori da verificare (sola lettura)
export const getProduttoriDaRivedereSummary = (importBatchId) =>
  api.get("/produttori/da-rivedere/summary", { params: importBatchId ? { importBatchId } : {} }).then((r) => r.data);

// Mezzi
export const getMezzi = () => api.get("/mezzi").then((r) => r.data);
export const createMezzo = (data) => api.post("/mezzi", data).then((r) => r.data);
export const updateMezzo = (id, data) => api.put(`/mezzi/${id}`, data).then((r) => r.data);
export const deleteMezzo = (id) => api.delete(`/mezzi/${id}`).then((r) => r.data);

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats").then((r) => r.data);

// Geocoding
export const getGeocodingStatus = () => api.get("/geocoding/status").then((r) => r.data);
export const startGeocoding = () => api.post("/geocoding/start").then((r) => r.data);
export const stopGeocoding = () => api.post("/geocoding/stop").then((r) => r.data);
export const geocodeSingle = (id) => api.post(`/geocoding/produttori/${id}`).then((r) => r.data);

// Import
export const importPreview = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const importExecute = (file, mapping) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mapping", JSON.stringify(mapping));
  return api.post("/import/execute", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const seedDatabase = (force = false) => api.post("/seed", null, { params: { force } }).then((r) => r.data);

// Settings
export const getDeposito = () => api.get("/settings/deposito").then((r) => r.data);
export const getFasce = () => api.get("/settings/fasce").then((r) => r.data);
export const setFasce = (cfg) => api.put("/settings/fasce", cfg).then((r) => r.data);

// Pianificazione (associazioni-slot) — RIMOSSA: modello Legacy eliminato.
// Non aggiungere qui nuove chiamate a /api/associazioni-slot.

// Giro del giorno
// getGiroToday rimosso: modello Legacy eliminato dal frontend (regola zone-only).
// Blocco B4 — preview giro modalità zone (sola lettura, no persistenza)
export const getGiroPreviewZone = (data) =>
  api.get("/giro/preview-zone", { params: { data } }).then((r) => r.data);
// Blocco B5 — giri operativi persistiti da zone.giornoServizio
export const createGiriFromZonePreview = (data, force = false) =>
  api.post(`/giro/from-zone-preview?data=${encodeURIComponent(data)}${force ? "&force=true" : ""}`).then((r) => r.data);
export const getGiriOperativiZone = (data) =>
  api.get("/giro/operativo-zone", { params: { data } }).then((r) => r.data);
export const assegnaGiro = (giroId, payload) =>
  api.put(`/giri/${giroId}/assegnazione`, payload).then((r) => r.data);
export const reoptimize = (ids) => api.post("/giri/optimize", { ids }).then((r) => r.data);
export const setGiroStato = (giroId, stato) => api.put(`/giri/${giroId}/stato`, { stato }).then((r) => r.data);
export const overrideMezzo = (giroId, mezzoId) => api.put(`/giri/${giroId}/override-mezzo`, { mezzoId }).then((r) => r.data);

// Storico cliente
export const getStoricoCliente = (pid) => api.get(`/produttori/${pid}/storico`).then((r) => r.data);

// Settings scadenze
export const getScadenze = () => api.get("/settings/scadenze").then((r) => r.data);
export const setScadenze = (cfg) => api.put("/settings/scadenze", cfg).then((r) => r.data);

// Dashboard mezzi oggi
export const getMezziOggi = () => api.get("/dashboard/mezzi-oggi").then((r) => r.data);

// Giri
export const getGiri = (params) => api.get("/giri", { params }).then((r) => r.data);
export const createGiro = (data) => api.post("/giri", data).then((r) => r.data);
export const updateGiro = (id, data) => api.put(`/giri/${id}`, data).then((r) => r.data);

// Ritiri
export const getRitiri = (params) => api.get("/ritiri", { params }).then((r) => r.data);
export const createRitiro = (data) => api.post("/ritiri", data).then((r) => r.data);
export const deleteRitiro = (id) => api.delete(`/ritiri/${id}`).then((r) => r.data);

// ─── Zone (Push 4 / BUG #11) ─────────────────────────────────────────────
export const listZone = (attive) => {
  const params = attive === undefined ? {} : { attive };
  return api.get("/zone", { params }).then((r) => r.data);
};
export const createZona = (data) => api.post("/zone", data).then((r) => r.data);
export const updateZona = (id, data) => api.put(`/zone/${id}`, data).then((r) => r.data);
export const disattivaZona = (id, sposta_in_zona_id) =>
  api.post(`/zone/${id}/disattiva`, { sposta_in_zona_id }).then((r) => r.data);
export const riattivaZona = (id) => api.post(`/zone/${id}/riattiva`).then((r) => r.data);

// Pianificazione contatori — RIMOSSA: modello Legacy eliminato.
// Non aggiungere qui nuove chiamate a /api/pianificazione/contatori.

// ─── Trasportatori attivi (per dropdown pianif - BUG #4) ─────────────────
export const listTrasportatoriAttivi = () =>
  api.get("/users/trasportatori-attivi").then((r) => r.data);
export const getTrasportatoreByMezzo = (mezzoId) =>
  api.get(`/users/by-mezzo/${mezzoId}`).then((r) => r.data);

// ─── Override trasportatore giro (BUG #V5) ────────────────────────────────
export const overrideTrasportatoreGiro = (giroId, trasportatoreId) =>
  api.put(`/giri/${giroId}/override-trasportatore`, { trasportatoreId }).then((r) => r.data);

// ─── Push 5: Giri Overrides ──────────────────────────────────────────────
export const listGiriOverrides = (data_giro) =>
  api.get("/giri/overrides", { params: data_giro ? { data_giro } : {} }).then((r) => r.data);
export const createGiroOverride = (payload) =>
  api.post("/giri/overrides", payload).then((r) => r.data);
export const deleteGiroOverride = (oid) =>
  api.delete(`/giri/overrides/${oid}`).then((r) => r.data);

// ─── Push 5: Produttore attivo/inattivo ──────────────────────────────────
export const toggleProduttoreAttivo = (id, attivo) =>
  api.post(`/produttori/${id}/toggle-attivo`, { attivo }).then((r) => r.data);

// ─── Push 5: Dashboard contratti scaduti ─────────────────────────────────
export const getContrattiScaduti = (limit = 10) =>
  api.get("/dashboard/contratti-scaduti", { params: { limit } }).then((r) => r.data);

// ─── Blocco B5.2: Drag&drop + Re-optimize sui giri operativi zone ────────
export const updateOrdineFermateZone = (giroId, ordineFermate) =>
  api.put(`/giri/${giroId}/ordine-fermate`, { ordineFermate }).then((r) => r.data);
export const reoptimizeGiroZone = (giroId) =>
  api.post(`/giri/${giroId}/reoptimize`).then((r) => r.data);

// ─── Blocco B5.5: Ritiri da recuperare ───────────────────────────────────
export const chiudiGiroConRecuperi = (giroId, payload) =>
  api.post(`/giri/${giroId}/chiudi-con-recuperi`, payload).then((r) => r.data);
export const listRecuperi = (params = {}) =>
  api.get("/recuperi", { params }).then((r) => r.data);
export const riprogrammaRecupero = (recId, payload) =>
  api.post(`/recuperi/${recId}/riprogramma`, payload).then((r) => r.data);
export const annullaRecupero = (recId, payload) =>
  api.post(`/recuperi/${recId}/annulla`, payload).then((r) => r.data);

// Riparazione stato incoerente: crea recuperi APERTO per fermate saltate
// di un giro già COMPLETATO/ANNULLATO che non hanno recupero.
export const generaRecuperiDaSaltate = (giroId) =>
  api.post(`/giri/${giroId}/genera-recuperi-da-saltate`).then((r) => r.data);
export const listSaltateSenzaRecupero = (giroId) =>
  api.get("/recuperi/saltate-senza-recupero", { params: giroId ? { giro_id: giroId } : {} }).then((r) => r.data);
